/* eslint-disable react/jsx-props-no-spreading */
import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import makeStyles from '@mui/styles/makeStyles';
import frLocale from 'date-fns/locale/fr';
import esLocale from 'date-fns/locale/es';
import enLocale from 'date-fns/locale/en-US';
import AdapterDateFns from '@mui/lab/AdapterDateFns';
import LocalizationProvider from '@mui/lab/LocalizationProvider';
import MobileDatePicker from '@mui/lab/MobileDatePicker';
import DesktopDatePicker from '@mui/lab/DatePicker';
import Stack from '@mui/material/Stack';
import { DateTime } from 'luxon';
import { TextField } from 'components/elements';
import { useDeviceDetect } from 'hooks';
import Styles from './styles';

const useStyles = makeStyles(Styles);

const configs = {
  en: { localeMap: enLocale },
  fr: { localeMap: frLocale },
  es: { localeMap: esLocale },
};

function CustomDatePicker({ label, error, helperText, value, onChange }) {
  const classes = useStyles();
  const { i18n } = useTranslation();
  const { isMobile } = useDeviceDetect();
  const DatePicker = isMobile ? MobileDatePicker : DesktopDatePicker;
  const [isOpen, setIsOpen] = useState(false);
  const config = useMemo(() => ({ locale: i18n.language, ...(configs[i18n.language] || configs.en) }), [i18n.language]);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} locale={config.localeMap}>
      <Stack spacing={3}>
        <DatePicker
          open={isOpen}
          disableFuture
          label={label}
          openTo="year"
          views={['year', 'month', 'day']}
          showToolbar={false}
          value={value}
          onChange={(newValue) => onChange(DateTime.fromJSDate(newValue))}
          renderInput={({ error: renderError, ...params }) => <TextField {...params} error={error || renderError} helperText={helperText} onClick={() => setIsOpen(true)} />}
          disableCloseOnSelect={false}
          disableHighlightToday
          disableOpenPicker
          onOpen={() => setIsOpen(true)}
          onClose={() => setIsOpen(false)}
          DialogProps={{ classes: { root: classes.dialog } }}
        />
      </Stack>
    </LocalizationProvider>
  );
}

CustomDatePicker.propTypes = {
  label: PropTypes.element.isRequired,
  error: PropTypes.bool,
  helperText: PropTypes.string,
  value: PropTypes.instanceOf(DateTime),
  onChange: PropTypes.func.isRequired,
};

CustomDatePicker.defaultProps = {
  value: null,
  helperText: null,
  error: false,
};

export default CustomDatePicker;
